import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router';
import {
  VaButton,
  VaModal,
} from '@department-of-veterans-affairs/component-library/dist/react-bindings';
import {
  WIZARD_STATUS,
  WIZARD_STATUS_RESTARTING,
} from '~/platform/site-wide/wizard';
import recordEvent from '../../monitoring/record-event';

import {
  CONTINUE_APP_DEFAULT_MESSAGE,
  START_NEW_APP_DEFAULT_MESSAGE,
  APP_TYPE_DEFAULT,
} from '../../forms-system/src/js/constants';

const FormStartControls = props => {
  const {
    formId,
    migrations,
    startPage,
    gaStartEventName,
    prefillAvailable,
    prefillTransformer,
    ariaLabel = null,
    ariaDescribedby = null,
    customStartLink = null,
    startActionLinkType,
    startText,
    formSaved,
    isExpired,
    resumeOnly,
  } = props;

  // get access to the formConfig object through this route, or from the
  // formConfig prop when rendered outside a route (the old destructure read
  // props.formConfig.formConfig, so a directly-passed formConfig never won)
  const formConfig = props.routes?.[1]?.formConfig || props.formConfig || {};

  const [modalOpen, setModalOpen] = useState(false);

  const goToBeginning = () => {
    props.router.push(startPage);
  };

  const captureAnalytics = () =>
    gaStartEventName && recordEvent({ event: gaStartEventName });

  const handleLoadPrefill = () => {
    captureAnalytics();
    if (prefillAvailable) {
      props.fetchInProgressForm(
        // TODO: where does this come from?
        formId,
        migrations,
        true,
        prefillTransformer,
      );
    } else {
      goToBeginning();
    }
  };

  const handleLoadForm = () =>
    // If successful, this will set form.loadedData.metadata.returnUrl and will
    //  trickle down to this.props to be caught in componentWillReceiveProps
    props.fetchInProgressForm(formId, migrations);

  const toggleModal = () => {
    setModalOpen(!modalOpen);
  };

  const startOver = () => {
    captureAnalytics();
    toggleModal();
    props.removeInProgressForm(formId, migrations, prefillTransformer);

    // Wizard status needs an intermediate value between not-started &
    // complete to prevent infinite loops in the RoutedSavableApp
    sessionStorage.setItem(
      formConfig?.wizardStorageKey || WIZARD_STATUS,
      WIZARD_STATUS_RESTARTING,
    );
  };

  const {
    appType = APP_TYPE_DEFAULT,
    continueAppButtonText = CONTINUE_APP_DEFAULT_MESSAGE,
    startNewAppButtonText = START_NEW_APP_DEFAULT_MESSAGE,
  } = formConfig?.customText || {};

  if (formSaved) {
    return (
      <div>
        {!isExpired && (
          <VaButton
            onClick={handleLoadForm}
            text={continueAppButtonText}
            label={ariaLabel}
            data-testid="continue-your-application"
            uswds
          />
        )}
        {!resumeOnly && (
          <VaButton
            class="mobile:vads-u-margin-top--1 mobile-lg:vads-u-margin-top--1"
            onClick={toggleModal}
            text={startNewAppButtonText}
            secondary={!isExpired}
            label={ariaLabel}
            uswds
          />
        )}
        <VaModal
          id="start-over-modal"
          status="warning"
          clickToClose
          modalTitle={`Starting over will delete your in-progress ${appType}.`}
          primaryButtonText={startNewAppButtonText}
          secondaryButtonText="Cancel"
          onCloseEvent={toggleModal}
          onPrimaryButtonClick={startOver}
          onSecondaryButtonClick={toggleModal}
          visible={modalOpen}
          uswds
        >
          {/* <h4>Starting over will delete your in-progress {appType}.</h4> */}
          <p>Are you sure you want to start over?</p>
        </VaModal>
      </div>
    );
  }

  // Left clicks are handled below (preventDefault + router.push startPage),
  // but middle clicks, ctrl/cmd clicks, and "open in new tab" navigate through
  // the href alone -- and startPage is a router path relative to the app's
  // basename, so as a raw href it resolves against the origin and 404s.
  // rootUrl is a real browser URL, and a cold visit to it lands on the
  // introduction page -- the same place RoutedSavableApp redirects any cold
  // mid-form entry. A real URL instead of a "#start" fragment also lets screen
  // readers announce this as a link to another page, not a same-page link.
  // (WCAG 4.1.2 Name, Role, Value)
  //
  // Without a rootUrl to point at, keep the fragment: an anchor with no href
  // has no link role at all, which is worse than the fragment it replaced.
  const startHref = formConfig?.rootUrl || '#start';

  if (customStartLink) {
    const CustomLink = customStartLink;
    return (
      <CustomLink
        href={startHref}
        data-testid="form-start-link"
        onClick={event => {
          event.preventDefault();
          handleLoadPrefill();
        }}
      >
        {startText}
      </CustomLink>
    );
  }

  const { useWebComponentForNavigation } = formConfig?.formOptions || {};

  return useWebComponentForNavigation || startActionLinkType ? (
    <va-link-action
      href={startHref}
      data-testid="form-start-link"
      onClick={event => {
        event.preventDefault();
        handleLoadPrefill();
      }}
      label={ariaLabel}
      message-aria-describedby={ariaDescribedby}
      text={startText}
      type={startActionLinkType}
    />
  ) : (
    <a
      href={startHref}
      className="vads-c-action-link--green"
      data-testid="form-start-link"
      onClick={event => {
        event.preventDefault();
        handleLoadPrefill();
      }}
      aria-label={ariaLabel}
      aria-describedby={ariaDescribedby}
    >
      {startText}
    </a>
  );
};

FormStartControls.propTypes = {
  fetchInProgressForm: PropTypes.func.isRequired,
  formId: PropTypes.string.isRequired,
  formSaved: PropTypes.bool.isRequired,
  prefillAvailable: PropTypes.bool.isRequired,
  removeInProgressForm: PropTypes.func.isRequired,
  router: PropTypes.object.isRequired,
  startPage: PropTypes.string.isRequired,
  ariaDescribedby: PropTypes.string,
  ariaLabel: PropTypes.string,
  customStartLink: PropTypes.any,
  formConfig: PropTypes.shape({
    customText: PropTypes.shape({
      startNewAppButtonText: PropTypes.string,
      continueAppButtonText: PropTypes.string,
    }),
  }),
  gaStartEventName: PropTypes.string,
  handleLoadPrefill: PropTypes.func,
  isExpired: PropTypes.bool,
  migrations: PropTypes.array,
  prefillTransformer: PropTypes.func,
  resumeOnly: PropTypes.bool,
  returnUrl: PropTypes.string,
  routes: PropTypes.array,
  startActionLinkType: PropTypes.oneOf(['primary', 'primary-entry']),
  startText: PropTypes.string,
  testActionLink: PropTypes.bool,
};

FormStartControls.defaultProps = {
  startText: 'Get Started',
  gaStartEventName: 'login-successful-start-form',
  testActionLink: false,
  // Empty so the customText destructure falls back to the default messages;
  // empty-string text here would override them now that a formConfig prop is
  // actually read (the old destructure ignored it)
  formConfig: {},
  ariaLabel: null,
  ariaDescribedby: null,
};

export default withRouter(FormStartControls);

export { FormStartControls };
